// Canvas 2D символы для элементов плана.
// Все функции рисуют в мировых координатах — трансформация уже применена через ctx.setTransform.

export type SymbolType =
  | 'lighting_single' | 'exhaust_fan' | 'lighting_spot' | 'switch' | 'switch_double'
  | 'socket_single' | 'socket_double' | 'socket_triple' | 'socket_quadruple' | 'socket_3ph' | 'bathroom_socket' | 'kitchen_socket'
  | 'washing_machine' | 'oven' | 'hob' | 'microwave' | 'extractor_hood'
  | 'refrigerator' | 'freezer' | 'air_conditioner' | 'boiler' | 'dryer'
  | 'electric_heater' | 'ev_charger' | 'hydromassage_bath' | 'dishwasher'
  | 'custom_block';

// Мощности (кВт) для расчёта суммарной нагрузки
export const KW_MAP: Record<string, number> = {
  lighting_single:  0.06,
  exhaust_fan:      0.10,
  lighting_spot:    0.05,
  switch:           0,
  switch_double:    0,
  socket_single:    0,
  socket_double:    0,
  socket_triple:    0,
  socket_quadruple: 0,
  socket_3ph:       0,
  bathroom_socket:  1.0,
  kitchen_socket:   2.0,
  washing_machine:  2.2,
  oven:             3.5,
  hob:              7.0,
  microwave:        1.2,
  extractor_hood:   0.3,
  refrigerator:     0.3,
  freezer:          0.3,
  air_conditioner:  2.0,
  boiler:           2.0,
  dryer:            2.5,
  electric_heater:  2.0,
  ev_charger:       3.6,
  hydromassage_bath: 1.5,
  dishwasher:       1.8,
  custom_block:     0,
};

// Категории для statusbar
export const GROUP_MAP: Record<string, 'light' | 'socket' | 'switch' | 'appliance'> = {
  lighting_single: 'light',
  exhaust_fan:     'light',
  lighting_spot:   'light',
  switch:          'switch',
  switch_double:   'switch',
  socket_single:   'socket',
  socket_double:   'socket',
  socket_triple:   'socket',
  socket_quadruple: 'socket',
  socket_3ph:      'socket',
  bathroom_socket: 'socket',
  kitchen_socket:  'socket',
  washing_machine: 'appliance',
  oven:            'appliance',
  hob:             'appliance',
  microwave:       'appliance',
  extractor_hood:  'appliance',
  refrigerator:    'appliance',
  freezer:         'appliance',
  air_conditioner: 'appliance',
  boiler:          'appliance',
  dryer:           'appliance',
  electric_heater: 'appliance',
  ev_charger:      'appliance',
  hydromassage_bath: 'appliance',
  dishwasher:      'appliance',
  custom_block:    'appliance',
};

// Цвета по категориям (тёмная тема проекта)
const COLOR: Record<string, string> = {
  light:     '#f59e0b', // accent-amber
  socket:    '#06b6d4', // accent-cyan
  switch:    '#8b9dc3', // ink-secondary
  appliance: '#10b981', // accent-green
};

function getColor(type: string): string {
  const group = GROUP_MAP[type] ?? 'appliance';
  return COLOR[group];
}

// ─── Освещение ───────────────────────────────────────────────────────────────

function drawLampCore(ctx: CanvasRenderingContext2D, cx: number, cy: number, c: string, r = 8) {
  ctx.strokeStyle = c;
  ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
  ctx.beginPath(); ctx.arc(cx, cy, 2, 0, Math.PI * 2); ctx.fillStyle = c; ctx.fill();
  for (let a = 0; a < Math.PI * 2; a += Math.PI / 4) {
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(a) * (r + 2), cy + Math.sin(a) * (r + 2));
    ctx.lineTo(cx + Math.cos(a) * (r + 6), cy + Math.sin(a) * (r + 6));
    ctx.stroke();
  }
}

function drawLightingSingle(ctx: CanvasRenderingContext2D, cx: number, cy: number) {
  ctx.save();
  drawLampCore(ctx, cx, cy, getColor('lighting_single'));
  ctx.restore();
}

function drawExhaustFan(ctx: CanvasRenderingContext2D, cx: number, cy: number) {
  ctx.save();
  const c = getColor('exhaust_fan');
  ctx.strokeStyle = c; ctx.fillStyle = c; ctx.lineWidth = 1.5;
  ctx.strokeRect(cx - 8, cy - 8, 16, 16);
  ctx.beginPath(); ctx.arc(cx, cy, 5.5, 0, Math.PI * 2); ctx.stroke();
  ctx.beginPath(); ctx.arc(cx, cy, 1.2, 0, Math.PI * 2); ctx.fill();
  for (let i = 0; i < 3; i += 1) {
    const angle = -Math.PI / 2 + i * (Math.PI * 2 / 3);
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.quadraticCurveTo(
      cx + Math.cos(angle + 0.7) * 3.5,
      cy + Math.sin(angle + 0.7) * 3.5,
      cx + Math.cos(angle) * 5,
      cy + Math.sin(angle) * 5,
    );
    ctx.stroke();
  }
  ctx.restore();
}

function drawLightingSpot(ctx: CanvasRenderingContext2D, cx: number, cy: number) {
  ctx.save();
  const c = getColor('lighting_spot');
  ctx.strokeStyle = c; ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.arc(cx, cy, 7, 0, Math.PI * 2); ctx.stroke();
  ctx.beginPath(); ctx.arc(cx, cy, 3.5, 0, Math.PI * 2);
  ctx.fillStyle = c; ctx.globalAlpha = 0.35; ctx.fill(); ctx.globalAlpha = 1;
  ctx.beginPath(); ctx.arc(cx, cy, 1.5, 0, Math.PI * 2); ctx.fill();
  ctx.restore();
}

// ─── Выключатель ─────────────────────────────────────────────────────────────

function drawSwitch(ctx: CanvasRenderingContext2D, cx: number, cy: number) {
  ctx.save();
  const c = getColor('switch');
  ctx.strokeStyle = c; ctx.lineWidth = 1.5;
  ctx.strokeRect(cx - 8, cy - 8, 16, 16);
  ctx.beginPath(); ctx.moveTo(cx - 3, cy + 1); ctx.lineTo(cx + 3, cy - 4); ctx.stroke();
  ctx.beginPath(); ctx.arc(cx + 3, cy - 4, 2, 0, Math.PI * 2);
  ctx.fillStyle = c; ctx.fill();
  ctx.restore();
}

// ─── Розетки ─────────────────────────────────────────────────────────────────

function drawSocketSingle(ctx: CanvasRenderingContext2D, cx: number, cy: number) {
  ctx.save();
  const c = getColor('socket_single');
  ctx.strokeStyle = c; ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.arc(cx, cy, 9, 0, Math.PI * 2); ctx.stroke();
  ctx.fillStyle = c;
  ctx.beginPath(); ctx.arc(cx - 3, cy - 2, 1.5, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(cx + 3, cy - 2, 1.5, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.moveTo(cx, cy + 2); ctx.lineTo(cx, cy + 5); ctx.stroke();
  ctx.restore();
}

function drawSocketDouble(ctx: CanvasRenderingContext2D, cx: number, cy: number) {
  ctx.save();
  const c = getColor('socket_double');
  ctx.strokeStyle = c; ctx.lineWidth = 1.3; ctx.fillStyle = c;
  ctx.beginPath(); ctx.arc(cx - 7, cy, 6.5, 0, Math.PI * 2); ctx.stroke();
  ctx.beginPath(); ctx.arc(cx - 9, cy - 2, 1.1, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(cx - 5, cy - 2, 1.1, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(cx + 7, cy, 6.5, 0, Math.PI * 2); ctx.stroke();
  ctx.beginPath(); ctx.arc(cx + 5, cy - 2, 1.1, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(cx + 9, cy - 2, 1.1, 0, Math.PI * 2); ctx.fill();
  ctx.restore();
}

function drawSocketTriple(ctx: CanvasRenderingContext2D, cx: number, cy: number) {
  ctx.save();
  const c = getColor('socket_triple');
  ctx.strokeStyle = c; ctx.lineWidth = 1.2; ctx.fillStyle = c;
  [-8, 0, 8].forEach(offset => {
    ctx.beginPath(); ctx.arc(cx + offset, cy, 4.6, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.arc(cx + offset - 1.2, cy - 1.2, 0.8, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(cx + offset + 1.2, cy - 1.2, 0.8, 0, Math.PI * 2); ctx.fill();
  });
  ctx.restore();
}

function drawSocketQuadruple(ctx: CanvasRenderingContext2D, cx: number, cy: number) {
  ctx.save();
  const c = getColor('socket_quadruple');
  ctx.strokeStyle = c; ctx.lineWidth = 1.2; ctx.fillStyle = c;
  [[-5, -5], [5, -5], [-5, 5], [5, 5]].forEach(([dx, dy]) => {
    ctx.beginPath(); ctx.arc(cx + dx, cy + dy, 4, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.arc(cx + dx - 1, cy + dy - 0.9, 0.7, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(cx + dx + 1, cy + dy - 0.9, 0.7, 0, Math.PI * 2); ctx.fill();
  });
  ctx.restore();
}

function drawSocket3ph(ctx: CanvasRenderingContext2D, cx: number, cy: number) {
  ctx.save();
  const c = getColor('socket_3ph');
  ctx.strokeStyle = c; ctx.lineWidth = 1.5; ctx.fillStyle = c;
  ctx.beginPath(); ctx.arc(cx, cy, 9, 0, Math.PI * 2); ctx.stroke();
  const pins: [number, number][] = [[cx, cy - 5], [cx - 4.3, cy + 2.5], [cx + 4.3, cy + 2.5]];
  pins.forEach(([px, py]) => {
    ctx.beginPath(); ctx.arc(px, py, 1.4, 0, Math.PI * 2); ctx.fill();
  });
  ctx.font = 'bold 5px monospace'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText('3~', cx, cy + 6.5);
  ctx.restore();
}

// ─── Бытовая техника (прямоугольники с буквами) ───────────────────────────

function drawApplianceBox(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number,
  label: string,
  color: string,
  w = 26, h = 20,
) {
  ctx.save();
  ctx.strokeStyle = color; ctx.lineWidth = 1.5;
  ctx.strokeRect(cx - w / 2, cy - h / 2, w, h);
  ctx.fillStyle = color; ctx.font = 'bold 8px monospace';
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText(label, cx, cy);
  ctx.restore();
}

// ─── Dispatch ─────────────────────────────────────────────────────────────────

type DrawFn = (ctx: CanvasRenderingContext2D, cx: number, cy: number) => void;

const DRAW_MAP: Record<string, DrawFn> = {
  lighting_single: drawLightingSingle,
  exhaust_fan:     drawExhaustFan,
  lighting_spot:   drawLightingSpot,
  switch:          drawSwitch,
  socket_single:   drawSocketSingle,
  socket_double:   drawSocketDouble,
  socket_triple:   drawSocketTriple,
  socket_quadruple: drawSocketQuadruple,
  socket_3ph:      drawSocket3ph,
  bathroom_socket: (ctx, cx, cy) => drawApplianceBox(ctx, cx, cy, 'ВН', '#06b6d4', 28, 20),
  kitchen_socket:  (ctx, cx, cy) => drawApplianceBox(ctx, cx, cy, 'К',  '#06b6d4'),
  washing_machine: (ctx, cx, cy) => drawApplianceBox(ctx, cx, cy, 'W',  '#10b981'),
  oven:            (ctx, cx, cy) => drawApplianceBox(ctx, cx, cy, 'Д',  '#10b981'),
  hob:             (ctx, cx, cy) => drawApplianceBox(ctx, cx, cy, 'ВП', '#10b981', 30, 20),
  microwave:       (ctx, cx, cy) => drawApplianceBox(ctx, cx, cy, 'М',  '#10b981'),
  extractor_hood:  (ctx, cx, cy) => drawApplianceBox(ctx, cx, cy, 'В',  '#10b981'),
  refrigerator:    (ctx, cx, cy) => drawApplianceBox(ctx, cx, cy, 'Х',  '#10b981'),
  freezer:         (ctx, cx, cy) => drawApplianceBox(ctx, cx, cy, 'МР', '#10b981', 28, 20),
  air_conditioner: (ctx, cx, cy) => drawApplianceBox(ctx, cx, cy, 'AC', '#10b981'),
  boiler:          (ctx, cx, cy) => drawApplianceBox(ctx, cx, cy, 'Б',  '#10b981'),
  dryer:           (ctx, cx, cy) => drawApplianceBox(ctx, cx, cy, 'С',  '#10b981'),
  electric_heater: (ctx, cx, cy) => drawApplianceBox(ctx, cx, cy, 'ЭО', '#10b981', 28, 20),
  ev_charger:      (ctx, cx, cy) => drawApplianceBox(ctx, cx, cy, 'EV', '#10b981', 28, 20),
  hydromassage_bath: (ctx, cx, cy) => drawApplianceBox(ctx, cx, cy, 'ГВ', '#10b981', 30, 20),
  dishwasher:      (ctx, cx, cy) => drawApplianceBox(ctx, cx, cy, 'ПМ', '#10b981', 28, 20),
};

export function drawSymbol(
  ctx: CanvasRenderingContext2D,
  type: string,
  cx: number,
  cy: number,
): void {
  const fn = DRAW_MAP[type];
  if (fn) fn(ctx, cx, cy);
}
